<script setup lang="ts">
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { stores } from '~/store';
  import { useFriendsStore } from '~/store/friends';

  const EFriendRequestTab = {
    SENT: 'sent',
    RECEIVED: 'received',
    REJECTED: 'rejected',
  } as const;
  type TFriendRequestTab = (typeof EFriendRequestTab)[keyof typeof EFriendRequestTab];

  const friendsStore = useFriendsStore();
  const { showErrorToast, showSuccessToast } = useAppToast();

  const tab = ref<TFriendRequestTab>(EFriendRequestTab.SENT);

  const friendRequestCategoriesTitles = Object.values(EFriendRequestTab);

  const friendRequestCategories = computed(() => ({
    [EFriendRequestTab.SENT]: friendsStore.sentFriends || [],
    [EFriendRequestTab.REJECTED]: friendsStore.rejectedFriends || [],
    [EFriendRequestTab.RECEIVED]: friendsStore.receivedFriends || [],
  }));

  async function acceptFriendRequest(item: IFriendData) {
    const response = await stores.firebase.acceptFriendRequest(item.friendId);

    if (response.status === 'SUCCESS') {
      showSuccessToast('Friend Request accepted');
    } else {
      showErrorToast(`Couldn't Accept Friend Request`);
    }
  }

  async function rejectFriendRequest(item: IFriendData) {
    const response = await stores.firebase.rejectFriendRequest(item.friendId);

    if (response.status === 'SUCCESS') {
      showSuccessToast('Friend Request rejected');
    } else {
      showErrorToast(`Couldn't reject friend request`);
    }
  }

  function setTab(item: typeof tab.value) {
    tab.value = item;
  }
</script>

<template>
  <div
    class="friend-container right flex flex-col px-0 pt-0 w-full max-w-[400px] max-h-[400px] md:max-h-none transition-opacity bg-[hsla(0,0%,100%,0.07)] rounded-xl h-full"
  >
    <GameFriendsListCardHeader title="FRIEND REQUEST">
      <template #icon>
        <SvgoAddFriendIcon class="size-10" />
      </template>
    </GameFriendsListCardHeader>

    <div class="space-y-4 pt-4">
      <div class="flex px-0 sm:px-3 gap-2">
        <CoreButton
          v-for="key in friendRequestCategoriesTitles"
          :key="key"
          :class="
            tab === key
              ? 'bg-primary hover:bg-primary active:bg-primary after:border-t-[10px]'
              : 'bg-white/5 hover:bg-white/5 active:bg-white/5'
          "
          class="flex-basis-1/3 basis-1/3 px-0 py-2 md:py-2 md:px-0 relative after:content-[''] after:absolute after:left-[42%] after:top-full after:w-0 after:h-0 after:border-l-[10px] after:border-l-transparent after:border-r-[10px] after:border-r-transparent after:border-t-[#2b7fff] after:clear-both"
          @click="setTab(key)"
        >
          <span class="text-[18px] font-bold uppercase whitespace-pre-wrap">
            {{ key }}
          </span>
        </CoreButton>
      </div>

      <div class="mt-8">
        <ul class="py-1 px-3 space-y-2 overflow-auto">
          <li
            v-if="!friendRequestCategories[tab].length"
            class="text-white text-center font-size-20 font-bold py-3 bg-white/5 rounded-xl"
          >
            Empty
          </li>
          <li
            v-for="(child, index) in friendRequestCategories[tab]"
            :key="index"
            class="flex items-center px-3 py-2 bg-white/5 rounded-lg"
          >
            <span class="text-white font-bold text-lg">
              {{ child.displayName || child.name }}
            </span>
            <div v-if="tab === EFriendRequestTab.RECEIVED" class="ml-auto flex gap-3">
              <UButton icon="i-heroicons-check" color="green" size="xs" @click="acceptFriendRequest(child)" />
              <UButton icon="i-heroicons-minus" color="error" size="xs" @click="rejectFriendRequest(child)" />
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
